import axios from "axios";
import { Company, EmployeePersona } from "../types";
import { hostname } from "../utils";

interface CompanyCreationOptions {
  getToken: Function;
  companyData: Company;
  currentUser?: any; // Unused; kept for caller compatibility
  chatApi?: any;
  whitelabelSettings?: any; // Unused; kept for caller compatibility
  onStatusUpdate?: (status: string) => void;
  createEmployees?: boolean;
  employeeCount?: number;
  selectedPersonas?: EmployeePersona[];
  useDefaultPublicEmployees?: boolean;
}

interface CompanyCreationResult {
  success: boolean;
  company?: Company;
  error?: string;
}

/**
 * Shared company creation logic. Lead-capture webhooks are now fired by the
 * backend during user/company creation — no webhook calls happen here.
 */
export async function createCompany(
  options: CompanyCreationOptions
): Promise<CompanyCreationResult> {
  const {
    getToken,
    companyData,
    chatApi,
    onStatusUpdate,
    createEmployees = true,
    employeeCount = 3,
    selectedPersonas,
    useDefaultPublicEmployees = false,
  } = options;

  try {
    onStatusUpdate?.("Creating your company...");
    await saveCompany(companyData, getToken);

    if (selectedPersonas && selectedPersonas.length > 0) {
      onStatusUpdate?.("Adding your AI team...");
      for (let i = 0; i < selectedPersonas.length; i++) {
        const persona = selectedPersonas[i];
        onStatusUpdate?.(`Adding ${persona.name || persona.title} to your team...`);
        await createEmployeeFromPersona(
          getToken,
          companyData.id,
          persona.id
        );
      }
    } else if (useDefaultPublicEmployees) {
      onStatusUpdate?.("Adding your AI team...");
      await addDefaultPublicEmployees(getToken, companyData.id);
    } else if (createEmployees && chatApi) {
      onStatusUpdate?.("Creating your AI team...");
      await chatApi.createEmployees(
        companyData,
        employeeCount,
        2,
        null,
        (newStatus: string) => onStatusUpdate?.(newStatus)
      );
    }

    return {
      success: true,
      company: companyData,
    };
  } catch (error: any) {
    console.error("Error creating company:", error);
    return {
      success: false,
      error: error.message || "Failed to create company",
    };
  }
}

async function saveCompany(
  companyData: Company,
  getToken: Function
): Promise<void> {
  const currentToken = await getToken();
  await axios.post(`${hostname}/companies`, companyData, {
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });
}

async function createEmployeeFromPersona(
  getToken: Function,
  companyId: string,
  personaId: string
): Promise<void> {
  const currentToken = await getToken();
  await axios.post(
    `${hostname}/employees/create-from-persona`,
    {
      companyId,
      personaId,
    },
    {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    }
  );
}

async function addDefaultPublicEmployees(
  getToken: Function,
  companyId: string
): Promise<void> {
  const currentToken = await getToken();
  await axios.post(
    `${hostname}/companies/${companyId}/add-default-employees`,
    {},
    {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    }
  );
}
